import { useParams, Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Sparkles, ArrowLeft, History, Wand2, Users, ChevronRight } from 'lucide-react'
import { ElfGenerator } from '@/client/components/ElfGenerator'
import { type ElfRace } from '@/client/lib/elves'
import { Meta } from '@/client/components/Meta'

// Content Data Map
const raceContent: Record<string, {
  name: ElfRace;
  tagline: string;
  description: string;
  history: string;
  namingRules: string;
  famousCharacters: { name: string; description: string }[];
  maleNames: { name: string; meaning: string }[];
  femaleNames: { name: string; meaning: string }[];
  emoji: string;
}> = {
  'high-elf': {
    name: 'High Elf',
    tagline: 'Legacy of the Stars',
    emoji: '🧝',
    description: 'High Elves (also known as Eladrin or Sun Elves) are the epitome of arcane elegance and ancient wisdom. Living for centuries, they dedicate their lives to the mastery of magic, philosophy, and the preservation of their celestial heritage.',
    history: 'High Elves trace their lineage back to the pure starlight of the First Age. Their empires once spanned continents, built on floating crystals and silver masonry. Unlike their wilder kin, High Elves maintained a strict hierarchy of high magic, believing that they are the chosen stewards of the world\'s ley lines.',
    namingRules: 'High Elven names are phonetics melodic, often utilizing bright vowel combinations like "Ae", "Ia", and "Eo". Prefixes typically denote celestial bodies or arcane concepts, while suffixes indicate noble lineage or military rank.',
    famousCharacters: [
      { name: 'Galadriel', description: 'The Lady of Lothlórien, bearer of Nenya.' },
      { name: 'Elrond', description: 'Master of Rivendell and a mighty healer.' }
    ],
    maleNames: [
        { name: "Aerindel Moonwhisper", meaning: "Light of the Moon of House Moonwhisper" }, { name: "Beluar Starflower", meaning: "Arcane Shield of House Starflower" }, { name: "Caladwen Sunwalker", meaning: "Morning Star of House Sunwalker" }, { name: "Draelor Silverleaf", meaning: "Ancient Spirit of House Silverleaf" }, { name: "Eldrin Arcaneheart", meaning: "Wise Protector of House Arcaneheart" },
        { name: "Faerand Celestian", meaning: "Spirit Voyager of House Celestian" }, { name: "Gaelen Aetherborn", meaning: "Silent Forest of House Aetherborn" }, { name: "Haelon Starlight", meaning: "Stone Singer of House Starlight" }, { name: "Iorveth Moonsong", meaning: "Eternal Justice of House Moonsong" }, { name: "Jaelen Highspire", meaning: "Sun Child of House Highspire" },
        { name: "Kaelen Glittergem", meaning: "Braveheart of House Glittergem" }, { name: "Luthien Brightbeam", meaning: "Enchanting Voice of House Brightbeam" }, { name: "Maelor Lorekeeper", meaning: "Great King of House Lorekeeper" }, { name: "Naelen Spellweaver", meaning: "Pure Soul of House Spellweaver" }, { name: "Oerindel Miststrider", meaning: "Sky Watcher of House Miststrider" }
    ],
    femaleNames: [
        { name: "Aelindra Moonwhisper", meaning: "Star Whisperer of House Moonwhisper" }, { name: "Celebrindal Starflower", meaning: "Silver Foot of House Starflower" }, { name: "Danalind Sunwalker", meaning: "Dew Drop of House Sunwalker" }, { name: "Elwing Silverleaf", meaning: "Star Spray of House Silverleaf" }, { name: "Fianna Arcaneheart", meaning: "Golden Vine of House Arcaneheart" },
        { name: "Gildis Celestian", meaning: "Star Flower of House Celestian" }, { name: "Hareth Aetherborn", meaning: "Stone Heart of House Aetherborn" }, { name: "Idril Starlight", meaning: "Sparkling Brilliance of House Starlight" }, { name: "Jezebel Moonsong", meaning: "Sun Queen of House Moonsong" }, { name: "Kethiel Highspire", meaning: "Kind Spirit of House Highspire" },
        { name: "Lirael Glittergem", meaning: "Lunar Song of House Glittergem" }, { name: "Melian Brightbeam", meaning: "Dear Gift of House Brightbeam" }, { name: "Nimrodel Lorekeeper", meaning: "White Lady of House Lorekeeper" }, { name: "Orophin Spellweaver", meaning: "Mountain Breeze of House Spellweaver" }, { name: "Pelindor Miststrider", meaning: "Peaceful Grove of House Miststrider" }
    ]
  },
  'dark-elf': {
    name: 'Dark Elf',
    tagline: 'Shadows of the Underdark',
    emoji: '🌑',
    description: 'Dark Elves, primarily known as the Drow, are a subterranean race known for their sophisticated but ruthless matriarchal society. They command the powers of shadow and spider-magic in the deep caverns of the world.',
    history: 'Banished from the surface world after the War of the Seldarine, the Dark Elves built sprawling obsidian cities in the Underdark. Their culture is one of constant intrigue, devotion to the Spider Queen, and mastery of poisons and silenced steel.',
    namingRules: 'Dark Elven names are sharp and rhythmic, often featuring "z", "x", "v", and "ss" sounds. They emphasize strength and nobility through harsh consonants that command attention.',
    famousCharacters: [
      { name: 'Drizzt Do\'Urden', description: 'A ranger who abandoned his people\'s evil ways.' },
      { name: 'Lolth', description: 'The spider-goddess and matron of the Drow.' }
    ],
    maleNames: [
        { name: "Drizzt Do'Urden", meaning: "Stealthy Hunter of House Do'Urden" }, { name: "Zaknafein Baenre", meaning: "Master of Blades of House Baenre" }, { name: "Jarlaxle Duskbringer", meaning: "Opportunist of House Duskbringer" }, { name: "Gromph Shadowseeker", meaning: "Ancient Power of House Shadowseeker" }, { name: "Malice Webweaver", meaning: "Poisonous Spirit of House Webweaver" },
        { name: "Vorn Nightfall", meaning: "Shadow Guard of House Nightfall" }, { name: "Dinin Spiderborn", meaning: "Quick Strike of House Spiderborn" }, { name: "Nalfein Venomkiss", meaning: "Silent Night of House Venomkiss" }, { name: "Durnad Voidbound", meaning: "Obsidian Will of House Voidbound" }, { name: "Rizzen Abysswalker", meaning: "Spider's Web of House Abysswalker" },
        { name: "Quenthel Obsidian", meaning: "Cavern Born of House Obsidian" }, { name: "Lyzandra Grimblade", meaning: "Moon Shadow of House Grimblade" }, { name: "Vorth Dreadlore", meaning: "Dark Blade of House Dreadlore" }, { name: "Zyx Maleficent", meaning: "Short Blade of House Maleficent" }, { name: "Vandis Silentstep", meaning: "Vengeance of House Silentstep" }
    ],
    femaleNames: [
        { name: "Lyzith Do'Urden", meaning: "Spider Mistress of House Do'Urden" }, { name: "Xunthila Baenre", meaning: "Silent Whisper of House Baenre" }, { name: "Varrina Duskbringer", meaning: "Night Queen of House Duskbringer" }, { name: "Zlydra Shadowseeker", meaning: "Venomous Grace of House Shadowseeker" }, { name: "Malyrra Webweaver", meaning: "Ancient Wisdom of House Webweaver" },
        { name: "Quenthel Nightfall", meaning: "Chosen of Lolth of House Nightfall" }, { name: "Larynda Spiderborn", meaning: "Moonlit Cavern of House Spiderborn" }, { name: "Vinnara Venomkiss", meaning: "Shadow Weaver of House Venomkiss" }, { name: "Zyxna Voidbound", meaning: "Crystal Shard of House Voidbound" }, { name: "Kaltis Abysswalker", meaning: "Ice Fire of House Abysswalker" },
        { name: "Nalfeina Obsidian", meaning: "Eternal Night of House Obsidian" }, { name: "Draxara Grimblade", meaning: "Dragon Queen of House Grimblade" }, { name: "Valthila Dreadlore", meaning: "Void Singer of House Dreadlore" }, { name: "Zarnna Maleficent", meaning: "Sharp Wit of House Maleficent" }, { name: "Lyzara Silentstep", meaning: "Obsidian Rose of House Silentstep" }
    ]
  },
  'wood-elf': {
    name: 'Wood Elf',
    tagline: 'Guardians of the Wild',
    emoji: '🌲',
    description: 'Wood Elves (or Copper Elves) are secluded and survival-focused kin who live in harmony with the ancient forests. They are masters of the bow and trackers of unparalleled skill.',
    history: 'Choosing the wild over the stone cities of their cousins, Wood Elves retreated into the primeval forests during the Great Sundering. Their history is spoken through the rustle of leaves and carved into the oldest oaks.',
    namingRules: 'Wood Elven names are earthy and short, often mimicking natural sounds like the wind, water, or snapping branches. They prefer prefixes derived from flora and fauna.',
    famousCharacters: [
      { name: 'Legolas Greenleaf', description: 'Prince of the Woodland Realm and member of the Fellowship.' },
      { name: 'Thranduil', description: 'The Elvenking of Mirkwood.' }
    ],
    maleNames: [
        { name: "Legolas Greenleaf", meaning: "Green Leaf of House Greenleaf" }, { name: "Thranduil Leafwalker", meaning: "Vigorous Spring of House Leafwalker" }, { name: "Haldir Swiftbow", meaning: "Tall Guardian of House Swiftbow" }, { name: "Celeborn Oakheart", meaning: "Silver Tree of House Oakheart" }, { name: "Oropher Thornrose", meaning: "High Beech of House Thornrose" },
        { name: "Galion Riverdance", meaning: "Bright Star of House Riverdance" }, { name: "Lindon Wildrunner", meaning: "Musical Land of House Wildrunner" }, { name: "Erestor Forestguide", meaning: "Lone Star of House Forestguide" }, { name: "Galdor Rootbound", meaning: "Branching Oak of House Rootbound" }, { name: "Rohan Deerfoot", meaning: "Red Horse of House Deerfoot" }
    ],
    femaleNames: [
        { name: "Arwen Evenstar", meaning: "Noble Maiden of House Evenstar" }, { name: "Tauriel Forestwalker", meaning: "Daughter of the Forest of House Forestwalker" }, { name: "Luthien Tinuviel", meaning: "Flower Maiden of House Tinuviel" }, { name: "Nimrodel Rainwhisper", meaning: "Lady of the Cave of House Rainwhisper" }, { name: "Celebrian Silverhand", meaning: "Silver Queen of House Silverhand" },
        { name: "Galadriel Goldenleaf", meaning: "Radiant Lady of House Goldenleaf" }, { name: "Idril Sparklefoot", meaning: "Sparkling Foot of House Sparklefoot" }, { name: "Eowyn Shieldmaiden", meaning: "Horselover of House Shieldmaiden" }, { name: "Adria Hawkshadow", meaning: "Dark One of House Hawkshadow" }, { name: "Amanra Windrider", meaning: "Wind of House Windrider" }
    ]
  },
  'christmas-elf': {
    name: 'Christmas Elf',
    tagline: 'Joy in Small Packages',
    emoji: '🎄',
    description: 'Christmas Elves are a cheerful, magical folk known for their incredible skill in crafting and their festive spirit. They dwell in the North Pole, working in harmony to spread joy across the realms.',
    history: 'Legend says they were born from the first laugh of winter. For centuries, they have lived in a hidden sanctuary beneath the aurora borealis, mastering the art of toy-making and reindeer husbandry.',
    namingRules: 'Names are typically short, playful, and often alliterative. They usually evoke winter themes, sweets, or sounds of celebration like "Jingle" or "Twinkle".',
    famousCharacters: [
      { name: 'Buddy', description: 'An unusually large elf who traveled the world in search of his origin.' },
      { name: 'Bernard', description: 'The stern but kind head of Santa\'s workshop.' }
    ],
    maleNames: [
        { name: "Jingle Gumdrop", meaning: "Joyful Sound of House Gumdrop" }, { name: "Buddy Snowball", meaning: "True Friend of House Snowball" }, { name: "Bernard Sugarplum", meaning: "Brave Bear of House Sugarplum" }, { name: "Twinkle Jingletree", meaning: "Bright Star of House Jingletree" }, { name: "Zippy Merrymaker", meaning: "Fast Worker of House Merrymaker" },
        { name: "Pippin Peppermint", meaning: "Small Fruit of House Peppermint" }, { name: "Buster Cocoabean", meaning: "Energetic of House Cocoabean" }, { name: "Tinsel Tinseltoe", meaning: "Shiny Decoration of House Tinseltoe" }, { name: "Snowball Frostybeard", meaning: "Winter Fun of House Frostybeard" }, { name: "Cocoa Northstar", meaning: "Warm Drink of House Northstar" }
    ],
    femaleNames: [
        { name: "Holly Evergreen", meaning: "Winter Green of House Evergreen" }, { name: "Ivy Mistletoe", meaning: "Ancient Vine of House Mistletoe" }, { name: "Joy Sparklejoy", meaning: "Pure Delight of House Sparklejoy" }, { name: "Belle Twinkle", meaning: "Beautiful Sound of House Twinkle" }, { name: "Merry Cookiebake", meaning: "Festive of House Cookiebake" },
        { name: "Noelle Wintersun", meaning: "Christmas Born of House Wintersun" }, { name: "Faith Northstar", meaning: "Deep Trust of House Northstar" }, { name: "Hope Brightlight", meaning: "Bright Future of House Brightlight" }, { name: "Star Nightglow", meaning: "Night Light of House Nightglow" }, { name: "Aurora Skydancer", meaning: "Northern Sky of House Skydancer" }
    ]
  },
  'half-elf': {
    name: 'Half-Elf',
    tagline: 'Best of Both Worlds',
    emoji: '🧬',
    description: 'Half-Elves are individuals born of both elven and human blood, often finding themselves as bridges between two cultures. They possess the longevity of elves and the adaptability of humans.',
    history: 'Arising from diplomatic marriages or fated romances, Half-Elves have no ancestral home of their own. Instead, they thrive in cosmopolitan cities where their unique blend of traits makes them natural leaders, diplomats, and adventurers.',
    namingRules: 'Naming varies wildly; some take elven names to honor their heritage, while others choose human names to fit into surface societies. Many blend the two styles for a unique identity.',
    famousCharacters: [
      { name: 'Tanis Half-Elven', description: 'The leader of the Companions in the Dragonlance saga.' },
      { name: 'Arwen', description: 'Though she had elven ancestry, she chose a mortal life for love.' }
    ],
    maleNames: [
        { name: "Tanis Whitewater", meaning: "Dual Identity of House Whitewater" }, { name: "Adran Silverhand", meaning: "Adventurous of House Silverhand" }, { name: "Bryn Oakshadow", meaning: "Hill dweller of House Oakshadow" }, { name: "Cael Moonchild", meaning: "Slender of House Moonchild" }, { name: "Doran Dawnwalker", meaning: "Exile of House Dawnwalker" },
        { name: "Eian Stormborn", meaning: "Gift of God of House Stormborn" }, { name: "Finn Ironheart", meaning: "Fair of House Ironheart" }, { name: "Gael Lightfoot", meaning: "Stranger of House Lightfoot" }, { name: "Hale Deepwater", meaning: "Healthy of House Deepwater" }, { name: "Ian Riverson", meaning: "God is Gracious of House Riverson" }
    ],
    femaleNames: [
        { name: "Adria Moonchild", meaning: "Dark One of House Moonchild" }, { name: "Bera Stormborn", meaning: "Bear of House Stormborn" }, { name: "Caya Silverhand", meaning: "Creek of House Silverhand" }, { name: "Dora Oakshadow", meaning: "Deer of House Oakshadow" }, { name: "Elia Whitewater", meaning: "Elk of House Whitewater" },
        { name: "Fara Dawnwalker", meaning: "Fawn of House Dawnwalker" }, { name: "Gara Ironheart", meaning: "Grass of House Ironheart" }, { name: "Hara Lightfoot", meaning: "Hawk of House Lightfoot" }, { name: "Ira Deepwater", meaning: "Iris of House Deepwater" }, { name: "Jara Riverson", meaning: "Juniper of House Riverson" }
    ]
  }
}

export function RacePage() {
  const { raceSlug } = useParams()
  const content = raceContent[raceSlug || 'high-elf'] || raceContent['high-elf']
  
  if (!content) return null // Type safety fallback

  return (
    <div className="flex flex-col">
      <Meta 
        title={`${content.name} Name Generator - Fantasy ${content.name} Names`}
        description={`Get the best ${content.name} names for your D&D characters and RPG stories. Explore ${content.name} lore, naming rules, and a curated list of 100 names.`}
        keywords={`${content.name.toLowerCase()} names, ${content.name.toLowerCase()} name generator, fantasy names, D&D names`}
      />
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden border-b border-border/40">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-transparent dark:from-primary/5" />
        <div className="container relative mx-auto max-w-6xl px-4">
          <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-8 transition-colors">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to All Generators
          </Link>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="outline" className="mb-4 text-primary border-primary/20 bg-primary/5">
                {content.tagline}
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
                {content.name} <br />
                <span className="text-primary">Name Generator</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed font-serif italic">
                "{content.description}"
              </p>
            </div>
            <div className="hidden lg:block relative text-center">
              <div className="aspect-square bg-gradient-to-br from-primary/20 to-accent/20 rounded-full blur-3xl absolute -inset-4 animate-pulse" />
              <div className="relative aspect-square flex items-center justify-center text-[10rem]">
                {content.emoji}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Generator Section */}
      <section className="py-24 bg-muted/20">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <Badge className="bg-primary hover:bg-primary/90">Exclusive Generator</Badge>
            <h2 className="text-3xl font-bold mt-4">Generate {content.name} Names</h2>
          </div>
          <ElfGenerator lockedRace={content.name} />
        </div>
      </section>

      {/* Deep Lore Section */}
      <section className="py-24 border-y border-border/40">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-primary">
                <History className="h-6 w-6" />
                <h3 className="text-xl font-bold">Historical Roots</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed text-sm">
                {content.history}
              </p>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-primary">
                <Wand2 className="h-6 w-6" />
                <h3 className="text-xl font-bold">Naming Phonetics</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed text-sm">
                {content.namingRules}
              </p>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-primary">
                <Users className="h-6 w-6" />
                <h3 className="text-xl font-bold">Legendary Figures</h3>
              </div>
              <div className="space-y-4">
                {content.famousCharacters.map(char => (
                  <div key={char.name} className="border-l-2 border-primary/20 pl-4 py-1">
                    <span className="font-bold block">{char.name}</span>
                    <span className="text-xs text-muted-foreground">{char.description}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Static Name Lists (SEO) */}
      <section className="py-24 bg-card">
        <div className="container mx-auto max-w-6xl px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Curated List of Traditional {content.name} Names</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Male Names */}
            <div>
              <div className="flex items-center gap-2 mb-6 pb-2 border-b">
                <Sparkles className="h-5 w-5 text-blue-500" />
                <h3 className="text-2xl font-bold">Male Names</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {content.maleNames.map(item => (
                  <div key={item.name} className="group">
                    <h3 className="text-lg font-bold group-hover:text-primary transition-colors">{item.name}</h3>
                    <p className="text-xs text-muted-foreground">Meaning: {item.meaning}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Female Names */}
            <div>
              <div className="flex items-center gap-2 mb-6 pb-2 border-b">
                <Sparkles className="h-5 w-5 text-pink-500" />
                <h3 className="text-2xl font-bold">Female Names</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {content.femaleNames.map(item => (
                  <div key={item.name} className="group">
                    <h3 className="text-lg font-bold group-hover:text-primary transition-colors">{item.name}</h3>
                    <p className="text-xs text-muted-foreground">Meaning: {item.meaning}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Races */}
      <section className="py-24 bg-muted/10 border-t border-border/40">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-2xl font-bold">Other Elven Lineages</h2>
            <Link to="/" className="text-primary flex items-center text-sm gap-1 hover:underline">
              View All <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { name: 'High Elf', slug: 'high-elf' },
              { name: 'Dark Elf', slug: 'dark-elf' },
              { name: 'Wood Elf', slug: 'wood-elf' },
              { name: 'Christmas Elf', slug: 'christmas-elf' },
              { name: 'Half-Elf', slug: 'half-elf' }
            ].filter(r => r.name !== content.name).map(race => (
              <Link key={race.slug} to={`/names/${race.slug}`}>
                <Card className="hover:bg-accent/50 transition-colors border-primary/10">
                  <CardContent className="p-4 flex items-center justify-between">
                    <span className="font-bold">{race.name} Names</span>
                    <ChevronRight className="h-4 w-4 opacity-30" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
